import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const useStyles = makeStyles((theme) => ({
  section: {
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
  },
  statGrid: {
    marginTop: theme.spacing(1),
  },
  statCard: {
    padding: theme.spacing(1.5),
    textAlign: "center",
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.spacing(0.5),
  },
  statValue: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  statLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  chip: {
    margin: theme.spacing(0.25),
    fontSize: "0.75rem",
    height: 22,
  },
  headerCell: {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  mono: {
    fontFamily: "monospace",
    fontSize: "0.8rem",
  },
  emptyState: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  revokeButton: {
    marginTop: theme.spacing(2),
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  },
}));

function StatCard({ label, value, classes }) {
  return (
    <Grid item xs={6} sm={4} md={3}>
      <div className={classes.statCard}>
        <Typography className={classes.statValue}>{value}</Typography>
        <Typography className={classes.statLabel}>{label}</Typography>
      </div>
    </Grid>
  );
}

function formatDate(dateVal) {
  if (!dateVal) return "—";
  const d = new Date(dateVal);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function formatRelative(dateVal) {
  if (!dateVal) return "—";
  const d = new Date(dateVal);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function GuardDeviceDrillIn({ device, events = [], onRevoke }) {
  const classes = useStyles();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!device) return null;

  return (
    <>
      <Paper className={classes.section}>
        <Typography className={classes.sectionTitle}>Device Details</Typography>
        <Grid container spacing={1} className={classes.statGrid}>
          <StatCard label="Platform" value={device.platform || "—"} classes={classes} />
          <StatCard label="OS Version" value={device.osVersion || "—"} classes={classes} />
          <StatCard label="Client Version" value={device.clientVersion || "—"} classes={classes} />
          <StatCard label="Arch" value={device.arch || "—"} classes={classes} />
          <StatCard label="Posture Tier" value={device.postureTier || "—"} classes={classes} />
          <StatCard label="Trust State" value={device.trustState || "—"} classes={classes} />
          <StatCard label="Attested" value={device.attested ? "yes" : "no"} classes={classes} />
          <StatCard label="Fork Suspected" value={device.forkSuspected ? "yes" : "no"} classes={classes} />
          <StatCard label="Last Heartbeat" value={formatRelative(device.lastHeartbeatAt)} classes={classes} />
          <StatCard label="First Seen" value={formatDate(device.firstSeenAt)} classes={classes} />
          <StatCard label="Last IP" value={device.lastIp || "—"} classes={classes} />
          <StatCard label="Country" value={device.lastCountry || "—"} classes={classes} />
        </Grid>
        {device.forkSuspected && device.lastForkReason && (
          <Typography variant="body2" style={{ marginTop: 8, color: "#ff6f00" }}>
            Fork reason: {device.lastForkReason}
          </Typography>
        )}
        {!device.revoked && (
          <Button
            variant="outlined"
            size="small"
            className={classes.revokeButton}
            onClick={() => setConfirmOpen(true)}
          >
            Revoke Device
          </Button>
        )}
        {device.revoked && (
          <div style={{ marginTop: 8 }}>
            <Chip label="REVOKED" size="small" className={classes.chip} style={{ backgroundColor: "#e53935", color: "#fff" }} />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              Revoked by {device.revokedBy || "unknown"}
              {device.revokedAt ? ` · ${formatDate(device.revokedAt)}` : ""}
            </Typography>
          </div>
        )}
      </Paper>

      {(device.ipHistory || []).length > 0 && (
        <Paper className={classes.section}>
          <Typography className={classes.sectionTitle}>
            IP History ({device.ipHistory.length})
          </Typography>
          <Divider style={{ marginBottom: 8 }} />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>IP</TableCell>
                  <TableCell className={classes.headerCell}>Country</TableCell>
                  <TableCell className={classes.headerCell}>At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {device.ipHistory.slice().reverse().map((entry, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell className={classes.mono}>{entry.ip}</TableCell>
                    <TableCell>{entry.country || "—"}</TableCell>
                    <TableCell>{formatDate(entry.at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Paper className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Diagnostic Reports ({(device.diagnosticsReports || []).length})
        </Typography>
        <Divider style={{ marginBottom: 8 }} />
        {(device.diagnosticsReports || []).length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>Uploaded</TableCell>
                  <TableCell className={classes.headerCell}>Client</TableCell>
                  <TableCell className={classes.headerCell}>Platform</TableCell>
                  <TableCell className={classes.headerCell}>Size</TableCell>
                  <TableCell className={classes.headerCell}>Report</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {device.diagnosticsReports.slice().reverse().map((report) => (
                  <TableRow key={report.reportId} hover>
                    <TableCell>{formatDate(report.uploadedAt)}</TableCell>
                    <TableCell className={classes.mono}>{report.clientVersion || "—"}</TableCell>
                    <TableCell>
                      {[report.platform, report.arch].filter(Boolean).join(" / ") || "—"}
                    </TableCell>
                    <TableCell>{formatBytes(report.sizeBytes)}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        href={`${basePath}/api/v7/admin/guard/devices/${device.deviceId}/diagnostics/${report.reportId}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download JSON
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography className={classes.emptyState}>No diagnostic reports uploaded.</Typography>
        )}
      </Paper>

      <Paper className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Security Events ({events.length})
        </Typography>
        <Divider style={{ marginBottom: 8 }} />
        {events.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>Type</TableCell>
                  <TableCell className={classes.headerCell}>Severity</TableCell>
                  <TableCell className={classes.headerCell}>Reason</TableCell>
                  <TableCell className={classes.headerCell}>At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((ev, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>
                      <Chip
                        label={ev.type || "unknown"}
                        size="small"
                        className={classes.chip}
                        style={{
                          backgroundColor: ev.severity === "alert" ? "#e53935" : "#757575",
                          color: "#fff",
                        }}
                      />
                    </TableCell>
                    <TableCell className={classes.mono}>{ev.severity || "—"}</TableCell>
                    <TableCell>{ev.reason || "—"}</TableCell>
                    <TableCell>{formatDate(ev.at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography className={classes.emptyState}>No security events recorded.</Typography>
        )}
      </Paper>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Revoke Device</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revoke device <strong>{device.deviceId}</strong>? The device will be blocked from future heartbeats. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              onRevoke(device.deviceId);
            }}
            style={{ color: "#e53935" }}
          >
            Revoke
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
